
import { platformApiFetch } from '@/services/platformApi/client';
import { AreaResult, Territory } from "@/types/supabase";

/**
 * Get all areas
 * @returns Array of areas
 */
export async function getAreas(): Promise<AreaResult[]> {
  return platformApiFetch<AreaResult[]>('/v1/areas');
}

/**
 * Get areas grouped by territory
 * @returns Object with territories as keys and arrays of areas as values
 */
export async function getAreasGroupedByTerritory(): Promise<Record<string, string[]>> {
  const areas = await getAreas();
  
  const grouped = areas.reduce((acc: Record<string, string[]>, area) => {
    if (!acc[area.territory]) {
      acc[area.territory] = [];
    }
    acc[area.territory].push(area.name);
    return acc;
  }, {});
  
  return grouped;
}

/**
 * Get all territories with their areas
 * @returns Array of territory objects with areas
 */
export async function getTerritories(): Promise<Territory[]> {
  const areas = await getAreas();
  
  const territoriesMap: Record<string, string[]> = {};
  
  areas.forEach(area => {
    if (!territoriesMap[area.territory]) {
      territoriesMap[area.territory] = [];
    }
    territoriesMap[area.territory].push(area.name);
  });
  
  const territories = Object.entries(territoriesMap)
    .map(([name, suburbs]) => ({
      name,
      suburbs: suburbs.sort((a, b) => a.localeCompare(b))
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  
  return territories;
}

/**
 * Get an area by ID
 * @param id Area ID
 * @returns Area object or null if not found
 */
export async function getAreaById(id: string): Promise<AreaResult | null> {
  try {
    const areas = await getAreas();
    return areas.find((area) => area.id === id) || null;
  } catch (error) {
    console.error("Error fetching area:", error);
    return null;
  }
}

/**
 * Create a new area
 * @param area Area object to create
 * @returns Created area or null if failed
 */
export async function createArea(area: { name: string; territory: string }): Promise<AreaResult | null> {
  return platformApiFetch<AreaResult | null>('/v1/areas', {
    method: 'POST',
    body: JSON.stringify(area),
  });
}

/**
 * Update an existing area
 * @param id Area ID
 * @param area Area data to update
 * @returns Updated area or null if failed
 */
export async function updateArea(id: string, area: { name?: string; territory?: string }): Promise<AreaResult | null> {
  return platformApiFetch<AreaResult | null>(`/v1/areas/${id}`, {
    method: 'PUT',
    body: JSON.stringify(area),
  });
}

/**
 * Delete an area
 * @param id Area ID
 * @returns true if successful, false otherwise
 */
export async function deleteArea(id: string): Promise<boolean> {
  return platformApiFetch<boolean>(`/v1/areas/${id}`, { method: 'DELETE' });
}
